import { Injectable, inject } from '@angular/core';
import { generateClient, type Client } from 'aws-amplify/api';
import { lastValueFrom } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { GraphqlSubscriptionResult } from '../interfaces/IGraphQL';
import { graphqlOptions } from '../graphql/graphql-options';

@Injectable({
    providedIn: 'root'
})
export class AppsyncService {
    private oidcSecurityService = inject(OidcSecurityService);

    public graphqlClient: Client;
    private initializingClientPromise?: Promise<Client>;
    // Track whether Amplify.configure has already been called to avoid redundant configuration
    private amplifyConfigured = false;

    private async initializeAmplifyAndGenerateClient(): Promise<Client> {
        if (this.graphqlClient) {
            return Promise.resolve(this.graphqlClient);
        }

        // If an initialization is already in progress, return the same promise
        if (this.initializingClientPromise) {
            return this.initializingClientPromise;
        }

        this.initializingClientPromise = import('aws-amplify')
            .then(({ Amplify }) => {
                if (!this.amplifyConfigured) {
                    Amplify.configure({
                        ...graphqlOptions
                    });
                    this.amplifyConfigured = true;
                }
                const client = generateClient() as unknown as Client;
                this.graphqlClient = client;
                return client;
            })
            .catch((err) => {
                this.initializingClientPromise = undefined;
                throw err;
            })
            .finally(() => {
                this.initializingClientPromise = undefined;
            });

        return this.initializingClientPromise;
    }

    public async listenTo<T>(query: string): Promise<GraphqlSubscriptionResult<T>> {
        const [token, client] = await Promise.all([
            lastValueFrom(this.oidcSecurityService.getIdToken()),
            this.initializeAmplifyAndGenerateClient()
        ]);
        return client.graphql({
            query,
            authToken: token
        }) as GraphqlSubscriptionResult<T>;
    }
}
